// What a creative's provider publishing actually looks like right now.
//
// Two sources describe it and they disagree by design. `Creative.sync` rides on
// the `cached` creatives list, so it is as old as that list's staleTime — fine
// for a grid, useless for narrating a publish in flight. `CreativePreviewStatus`
// is the `realtime` resource that polls until the sync is terminal. This is the
// one place that decides which one wins, so a screen cannot get it wrong.
//
// It exists as a module because logic left inline in `library.tsx` was, while
// that screen went unchecked, unreachable by `tsc` AND by every test — how a Retry
// button shipped that invalidated a resource the screen does not read and left
// the panel reporting `failed` on a creative that had already published.

import type { CreativePreviewStatus, CreativeSyncFailureCode } from '@/api/manage-types';
import type { FailureView } from '@/api/types.gen';

/** Mirrors `PROVIDER_RETRY_ATTEMPT_CAP` in `api/src/queue/mod.rs`. */
export const PROVIDER_RETRY_ATTEMPT_CAP = 10;

export interface CreativeSyncView {
  /** `null` when nothing has been enqueued yet. */
  phase: string | null;
  /** No further transition is coming without an operator action. */
  settled: boolean;
  failureCode: CreativeSyncFailureCode | null;
  /** The failure in words, from the same live status; `null` on older responses. */
  failure: FailureView | null;
  /** The provider's own id — a Meta AdCreative id, or TTD's equivalent. */
  providerCreativeId: string | null;
  /**
   * The background retry cron has given up.
   *
   * Nothing surfaced this before, so a creative the cron had abandoned looked
   * exactly like one that had failed once and would be retried — which is what
   * turned the 2026-08-17 deploy into a manual hunt for a stranded row.
   */
  retriesExhausted: boolean;
}

interface SyncSnapshot {
  status?: string | null;
  external_id?: string | null;
  last_error?: string | null;
}

export function creativeSyncView(
  channel: string,
  sync: SyncSnapshot | null | undefined,
  live: CreativePreviewStatus | null | undefined,
): CreativeSyncView {
  // The live status is per-target; a creative has exactly one.
  const livePhase = channel === 'meta' ? live?.meta_status : live?.ttd_status;
  const phase = livePhase ?? sync?.status ?? null;
  return {
    phase,
    settled: phase === 'synced' || phase === 'failed',
    failureCode: live?.last_error ?? null,
    failure: live?.failure ?? null,
    providerCreativeId: live?.meta?.adcreative_id ?? sync?.external_id ?? null,
    retriesExhausted: (live?.attempts ?? 0) >= PROVIDER_RETRY_ATTEMPT_CAP,
  };
}

/** The word an operator reads in the destinations panel. */
export function creativeSyncLabel(view: CreativeSyncView): string {
  if (view.phase === 'synced') return 'published';
  if (view.phase === 'failed') return 'failed';
  return view.phase ? 'publishing…' : 'queued';
}
